import { ExportMenu } from "@/components/ExportMenu";
import Pagination from "@/components/Pagination";
import { useEffect, useState } from "react";
import api, { money, fmtDate, apiErr, waLink, invoiceText, within24h } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import { useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { newUid } from "@/lib/deviceId";
import StatusBadge from "@/components/StatusBadge";
import Modal from "@/components/Modal";
import VoidReasonDialog from "@/components/VoidReasonDialog";
import { HandCoins, Search, MessageCircle, Ban, Check } from "lucide-react";

export default function Receivables() {
  const { current, settings } = useActivity();
  const { user } = useAuth();
  const { offlineAction, deviceId } = useOffline();
  const canVoid = ["owner", "admin", "manager"].includes(user?.role) || !!user?.can_void_sales;
  const sendInvoiceWA = (sale, phone) => {
    if (!phone) return toast.error("Aucun numéro enregistré pour ce client");
    const text = invoiceText(sale, settings.invoice_template);
    window.open(waLink(phone, text), "_blank", "noopener,noreferrer");
  };
  const [data, setData] = useState({ rows: [], total_due: 0, count: 0 });
  const [phones, setPhones] = useState({});
  const [names, setNames] = useState({});
  const [payments, setPayments] = useState([]);
  const [payPage, setPayPage] = useState(1);
  const [payHideVoided, setPayHideVoided] = useState(true);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidLoading, setVoidLoading] = useState(false);
  const [voidErr, setVoidErr] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);
  const [saleDetail, setSaleDetail] = useState(null);
  const openDetail = async (id) => { setSaleDetail({ loading: true }); try { const { data } = await api.get(`/sales/${id}${current ? `?activity_id=${current.id}` : ""}`); setSaleDetail(data); } catch (e) { setSaleDetail(null); toast.error(apiErr(e.response?.data?.detail)); } };

  const [page, setPage] = useState(1);
  const [salesHist, setSalesHist] = useState([]);
  const [histPage, setHistPage] = useState(1);
  const [histPeriod, setHistPeriod] = useState("all");
  const [histFrom, setHistFrom] = useState("");
  const [histTo, setHistTo] = useState("");
  const [histQ, setHistQ] = useState("");
  const load = async () => {
    const url = current ? `/receivables?activity_id=${current.id}` : "/receivables";
    const reqs = [api.get(url), api.get(current ? `/parties?kind=customer&activity_id=${current.id}` : "/parties?kind=customer")];
    if (canVoid) reqs.push(api.get(current ? `/payments?activity_id=${current.id}` : "/payments"));
    const [res, p, pay] = await Promise.all(reqs);
    setData(res.data);
    setPhones(Object.fromEntries(p.data.map((x) => [x.id, x.phone])));
    setNames(Object.fromEntries(p.data.map((x) => [x.id, x.name])));
    if (pay) setPayments(pay.data);
  };
  useEffect(() => { load(); }, [current]);

  const ymd = (d) => { const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
  const periodRange = (period) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (period === "today") return { from: ymd(today), to: ymd(today) };
    if (period === "yesterday") { const y = new Date(today); y.setDate(y.getDate() - 1); return { from: ymd(y), to: ymd(y) }; }
    if (period === "week") { const wd = (today.getDay() + 6) % 7; const mon = new Date(today); mon.setDate(mon.getDate() - wd); return { from: ymd(mon), to: ymd(today) }; }
    if (period === "month") { const first = new Date(today.getFullYear(), today.getMonth(), 1); return { from: ymd(first), to: ymd(today) }; }
    return { from: "", to: "" };
  };
  const payLabel = (t) => ({ cash: "Comptant", partial: "Partiel", credit: "Crédit" }[t] || t || "—");
  const loadHistory = async () => {
    let from = "", to = "";
    if (histPeriod === "custom") { from = histFrom; to = histTo; }
    else if (histPeriod !== "all") { const r = periodRange(histPeriod); from = r.from; to = r.to; }
    const params = new URLSearchParams();
    if (current) params.set("activity_id", current.id);
    if (from) params.set("from_date", from);
    if (to) params.set("to_date", to);
    try { const { data } = await api.get(`/sales-history?${params.toString()}`); setSalesHist(data.rows || []); setHistPage(1); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  useEffect(() => { loadHistory(); }, [current, histPeriod, histFrom, histTo]);
  const histShown = salesHist.filter((s) => { const t = histQ.trim().toLowerCase(); if (!t) return true; return (s.number || "").toLowerCase().includes(t) || (s.customer_name || "").toLowerCase().includes(t) || (s.seller_name || "").toLowerCase().includes(t); });

  const visiblePayments = payments.filter((p) => !payHideVoided || !p.voided);

  const doVoidPayment = async (reason) => {
    if (!voidTarget) return;
    setVoidLoading(true); setVoidErr("");
    try {
      await api.post(`/payments/${voidTarget.id}/void`, { reason });
      toast.success("Encaissement annulé · créance rétablie");
      setVoidTarget(null); load();
    } catch (e) { setVoidErr(apiErr(e.response?.data?.detail)); }
    finally { setVoidLoading(false); }
  };

  const rows = data.rows.filter((r) => (r.customer_name || "").toLowerCase().includes(q.toLowerCase()) || (r.number || "").toLowerCase().includes(q.toLowerCase()));

  const openPay = (sale) => { setSel(sale); setAmount(String(sale.balance)); setMethod("cash"); };

  const pay = async () => {
    setBusy(true);
    const uid = newUid();
    const payload = {
      activity_id: sel.activity_id, customer_id: sel.customer_id, amount: Number(amount),
      method, account: method === "cash" ? "Espèces" : method,
      allocations: [{ sale_id: sel.id, amount: Number(amount) }],
      reference: `Encaissement ${sel.number}`, client_uid: uid, device_id: deviceId, offline: !navigator.onLine,
    };
    if (!navigator.onLine) {
      await offlineAction({ uid, type: "api", method: "post", url: "/payments", payload,
        localSale: { number: `Encaissement ${sel.number}`, total: Number(amount) } });
      toast.success("Encaissement enregistré hors-ligne (en attente de synchronisation)");
      setSel(null); setBusy(false); return;
    }
    try {
      await api.post("/payments", payload);
      toast.success("Encaissement enregistré · reçu généré");
      setSel(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 min-w-0 max-w-full overflow-x-hidden" data-testid="receivables-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Portefeuille des créances</h1>
          <p className="text-sm text-[#64748B] mt-1">{data.count} créance(s) · total dû <span className="font-semibold text-[#B45309]">{money(data.total_due)}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu dataset="receivables" params={{}} />
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} className="inp pl-9" placeholder="Client ou n° de vente…" data-testid="receivables-search" />
        </div>
      </div>

      <div className="card">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <th className="th">N°</th><th className="th">Client</th><th className="th">Date</th>
              <th className="th text-right">Total</th><th className="th text-right">Payé</th>
              <th className="th text-right">Solde</th><th className="th">Statut</th><th className="th text-right">Action</th>
            </tr></thead>
            <tbody>
              {rows.slice((page - 1) * 10, page * 10).map((r) => (
                <tr key={r.id} className="row" data-testid={`receivable-row-${r.id}`}>
                  <td className="td font-mono text-xs">{r.number}</td>
                  <td className="td font-medium text-[#0F172A]">{r.customer_name}</td>
                  <td className="td text-xs">{fmtDate(r.created_at)}</td>
                  <td className="td text-right">{money(r.total)}</td>
                  <td className="td text-right text-[#15803D]">{money(r.paid)}</td>
                  <td className="td text-right font-semibold text-[#B45309]">{money(r.balance)}</td>
                  <td className="td"><StatusBadge status={r.status} /></td>
                  <td className="td text-right whitespace-nowrap">
                    <button onClick={() => openDetail(r.id)} className="btn-outline !py-1.5 !px-2.5 mr-1" data-testid={`sale-detail-${r.id}`} title="Détails de la vente">Détails</button>
                    <button onClick={() => sendInvoiceWA(r, phones[r.customer_id])} disabled={!phones[r.customer_id]} className="btn-outline !py-1.5 !px-2.5 mr-1 !text-[#15803D] disabled:opacity-40" data-testid={`wa-invoice-${r.id}`} title="Envoyer la facture sur WhatsApp"><MessageCircle className="w-4 h-4" /></button>
                    <button onClick={() => openPay(r)} data-testid={`collect-btn-${r.id}`} className="btn-primary !py-1.5 !px-3"><HandCoins className="w-4 h-4" /> Encaisser</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="td text-center text-[#94A3B8] py-10">Aucune créance en cours</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-[#E2E8F0]">
          {rows.slice((page - 1) * 10, page * 10).map((r) => (
            <div key={r.id} className="py-4 first:pt-1" data-testid={`receivable-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-[#0F172A] truncate">{r.customer_name}</div>
                  <div className="text-xs text-[#94A3B8] mt-0.5 font-mono">{r.number} · {fmtDate(r.created_at)}</div>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-3 flex items-end justify-between gap-3 border-t border-[#EEF2F7] pt-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] font-semibold">Solde dû</div>
                  <div className="text-2xl font-extrabold text-[#B45309] font-head leading-tight">{money(r.balance)}</div>
                </div>
                <div className="text-right text-xs text-[#64748B] leading-relaxed">
                  <div>Total {money(r.total)}</div>
                  <div className="text-[#15803D]">Payé {money(r.paid)}</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2 min-w-0">
                <button onClick={() => openDetail(r.id)} className="btn-outline !py-2.5 !px-3 shrink-0" data-testid={`sale-detail-m-${r.id}`} title="Détails de la vente">Détails</button>
                <button onClick={() => sendInvoiceWA(r, phones[r.customer_id])} disabled={!phones[r.customer_id]} className="btn-outline !py-2.5 !px-3 !text-[#15803D] disabled:opacity-40 shrink-0" data-testid={`wa-invoice-m-${r.id}`} title="Envoyer la facture sur WhatsApp"><MessageCircle className="w-4 h-4" /></button>
                <button onClick={() => openPay(r)} data-testid={`collect-btn-m-${r.id}`} className="btn-primary flex-1 min-w-0 justify-center !py-2.5"><HandCoins className="w-4 h-4" /> Encaisser</button>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="text-center text-[#94A3B8] py-10">Aucune créance en cours</div>}
        </div>

        <Pagination page={page} setPage={setPage} total={rows.length} />
      </div>

      {/* ---- Historique des ventes / Encaissements ---- */}
      <div data-testid="sales-history-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-lg font-semibold font-head text-[#16324F]">Historique des ventes / Encaissements</h3>
          <span className="text-sm text-[#64748B]" data-testid="sales-history-count">{histShown.length} vente(s)</span>
        </div>
        <div className="card p-4 mb-3">
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input value={histQ} onChange={(e) => { setHistQ(e.target.value); setHistPage(1); }} className="inp pl-9" placeholder="Rechercher par client ou n° de vente…" data-testid="hist-search" />
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3 min-w-0">
            <div className="flex flex-wrap gap-1.5">
              {[["today", "Aujourd'hui"], ["yesterday", "Hier"], ["week", "Cette semaine"], ["month", "Ce mois-ci"]].map(([k, lbl]) => (
                <button key={k} onClick={() => { setHistPeriod(k); setHistFrom(""); setHistTo(""); }} data-testid={`hist-filter-${k}`}
                  className={`px-3 py-1.5 min-h-[36px] rounded-lg text-sm font-medium border transition-colors ${histPeriod === k ? "bg-[#16324F] text-white border-[#16324F]" : "bg-white text-[#334155] border-[#E2E8F0] hover:bg-[#F1F5F9]"}`}>{lbl}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:flex sm:items-end gap-2 sm:ml-auto min-w-0">
              <div className="min-w-0"><label className="lbl !text-[11px] !mb-0.5">Date de début</label><input type="date" value={histFrom} onChange={(e) => { setHistFrom(e.target.value); setHistPeriod("custom"); }} className="inp !py-1.5 w-full min-w-0" data-testid="hist-from-date" /></div>
              <div className="min-w-0"><label className="lbl !text-[11px] !mb-0.5">Date de fin</label><input type="date" value={histTo} onChange={(e) => { setHistTo(e.target.value); setHistPeriod("custom"); }} className="inp !py-1.5 w-full min-w-0" data-testid="hist-to-date" /></div>
              <button onClick={() => { setHistPeriod("all"); setHistFrom(""); setHistTo(""); }} data-testid="hist-filter-reset" className="btn-outline !py-1.5 !px-3 col-span-2 sm:col-span-1 justify-center min-h-[36px]">Réinitialiser</button>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full" data-testid="sales-history-table">
              <thead><tr>
                <th className="th">Référence</th><th className="th">Date / heure</th><th className="th">Client</th>
                <th className="th text-right">Montant</th><th className="th">Paiement</th><th className="th">Vendeur</th><th className="th text-right">Action</th>
              </tr></thead>
              <tbody>
                {histShown.slice((histPage - 1) * 10, histPage * 10).map((s) => (
                  <tr key={s.id} className="row" data-testid={`hist-row-${s.id}`}>
                    <td className="td font-mono text-xs">{s.number}</td>
                    <td className="td text-xs">{fmtDate(s.created_at)}</td>
                    <td className="td font-medium text-[#0F172A]">{s.customer_name || "Comptant"}</td>
                    <td className="td text-right">{money(s.total)}</td>
                    <td className="td whitespace-nowrap"><span className="text-xs text-[#64748B] mr-1">{payLabel(s.payment_type)}</span><StatusBadge status={s.status} /></td>
                    <td className="td text-xs">{s.seller_name || "—"}</td>
                    <td className="td text-right"><button onClick={() => openDetail(s.id)} className="btn-outline !py-1.5 !px-2.5" data-testid={`hist-detail-${s.id}`}>Détails</button></td>
                  </tr>
                ))}
                {histShown.length === 0 && <tr><td colSpan={7} className="td text-center text-[#94A3B8] py-10">{histQ.trim() ? "Aucune vente ne correspond à votre recherche" : "Aucune vente sur cette période"}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {histShown.slice((histPage - 1) * 10, histPage * 10).map((s) => (
              <div key={s.id} className="py-4 first:pt-1" data-testid={`hist-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0F172A] truncate">{s.customer_name || "Comptant"}</div>
                    <div className="text-xs text-[#94A3B8] mt-0.5 font-mono">{s.number} · {fmtDate(s.created_at)}</div>
                    <div className="text-xs text-[#64748B] mt-0.5">Vendeur : {s.seller_name || "—"} · {payLabel(s.payment_type)}</div>
                  </div>
                  <div className="text-right shrink-0"><div className="font-bold text-[#0F172A]">{money(s.total)}</div><div className="mt-1"><StatusBadge status={s.status} /></div></div>
                </div>
                <div className="mt-3"><button onClick={() => openDetail(s.id)} className="btn-outline !py-2 !px-3 w-full justify-center" data-testid={`hist-detail-m-${s.id}`}>Détails</button></div>
              </div>
            ))}
            {histShown.length === 0 && <div className="text-center text-[#94A3B8] py-10">{histQ.trim() ? "Aucune vente ne correspond à votre recherche" : "Aucune vente sur cette période"}</div>}
          </div>
          <Pagination page={histPage} setPage={setHistPage} total={histShown.length} />
        </div>
      </div>

      {canVoid && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-lg font-semibold font-head text-[#16324F]">Encaissements récents</h3>
            <label className="flex items-center gap-2 text-sm text-[#475569] whitespace-nowrap cursor-pointer">
              <input type="checkbox" checked={payHideVoided} onChange={(e) => { setPayHideVoided(e.target.checked); setPayPage(1); }} data-testid="pay-hide-voided" className="w-4 h-4" />
              Masquer les encaissements annulés
            </label>
          </div>
          <div className="card">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead><tr>
                  <th className="th">Date</th><th className="th">Client</th><th className="th">Référence</th>
                  <th className="th">Mode</th><th className="th text-right">Montant</th><th className="th">Statut</th><th className="th text-right">Action</th>
                </tr></thead>
                <tbody>
                  {visiblePayments.slice((payPage - 1) * 10, payPage * 10).map((p) => (
                    <tr key={p.id} className={`row ${p.voided ? "opacity-60" : ""}`} data-testid={`payment-row-${p.id}`}>
                      <td className="td text-xs">{fmtDate(p.created_at)}</td>
                      <td className="td font-medium text-[#0F172A]">{p.customer_id ? (names[p.customer_id] || "—") : <span className="text-[#64748B]">Client comptoir</span>}</td>
                      <td className="td text-xs">{p.reference || "—"}</td>
                      <td className="td text-xs">{p.method === "cash" ? "Espèces" : p.method === "mobile_money" ? "Mobile Money" : p.method === "bank" ? "Banque" : p.method === "store_credit" ? "Avoir" : p.method}</td>
                      <td className={`td text-right font-semibold ${p.voided ? "line-through text-[#94A3B8]" : "text-[#15803D]"}`}>{money(p.amount)}</td>
                      <td className="td">{p.voided ? (
                    <div>
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] px-2.5 py-0.5 text-xs font-semibold">Annulé</span>
                      {p.voided_by && <div className="text-[10px] text-[#94A3B8] mt-1 max-w-[190px] leading-tight" title={p.voided_reason || ""}>par {p.voided_by}{p.voided_at ? ` · ${fmtDate(p.voided_at)}` : ""}</div>}
                    </div>
                  ) : <span className="inline-flex items-center gap-1 rounded-full border border-[#bbf7d0] bg-[#DCFCE7] text-[#15803D] px-2.5 py-0.5 text-xs font-semibold">Encaissé</span>}</td>
                      <td className="td text-right">
                        {!p.voided && within24h(p.created_at)
                          ? <button onClick={() => { setVoidErr(""); setVoidTarget(p); }} className="btn-remove !py-1.5 !px-3" data-testid={`void-payment-${p.id}`}><Ban className="w-3.5 h-3.5" /> Annuler</button>
                          : !p.voided ? <span className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B] px-2.5 py-0.5 text-xs font-semibold" data-testid={`payment-validated-${p.id}`}><Check className="w-3 h-3" /> Validé</span> : null}
                      </td>
                    </tr>
                  ))}
                  {visiblePayments.length === 0 && <tr><td colSpan={7} className="td text-center text-[#94A3B8] py-10">Aucun encaissement{payHideVoided ? " actif" : ""}</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-[#E2E8F0]">
              {visiblePayments.slice((payPage - 1) * 10, payPage * 10).map((p) => (
                <div key={p.id} className={`py-4 first:pt-1 ${p.voided ? "opacity-60" : ""}`} data-testid={`payment-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F172A] truncate">{p.customer_id ? (names[p.customer_id] || "—") : "Client comptoir"}</div>
                      <div className="text-xs text-[#94A3B8] mt-0.5">{fmtDate(p.created_at)} · {p.method === "cash" ? "Espèces" : p.method === "mobile_money" ? "Mobile Money" : p.method === "bank" ? "Banque" : p.method === "store_credit" ? "Avoir" : p.method}</div>
                      {p.reference && <div className="text-xs text-[#64748B] mt-0.5 truncate">{p.reference}</div>}
                    </div>
                    {p.voided ? <span className="inline-flex items-center gap-1 rounded-full border border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] px-2.5 py-0.5 text-xs font-semibold shrink-0">Annulé</span> : <span className="inline-flex items-center gap-1 rounded-full border border-[#bbf7d0] bg-[#DCFCE7] text-[#15803D] px-2.5 py-0.5 text-xs font-semibold shrink-0">Encaissé</span>}
                  </div>
                  <div className={`mt-2 text-lg font-bold ${p.voided ? "line-through text-[#94A3B8]" : "text-[#15803D]"}`}>{money(p.amount)}</div>
                  {p.voided && p.voided_by && <div className="mt-1 text-[11px] text-[#94A3B8] leading-tight">Annulé par {p.voided_by}{p.voided_at ? ` · ${fmtDate(p.voided_at)}` : ""}{p.voided_reason ? ` · ${p.voided_reason}` : ""}</div>}
                  {!p.voided && within24h(p.created_at)
                    ? <div className="mt-3"><button onClick={() => { setVoidErr(""); setVoidTarget(p); }} className="btn-remove !py-1.5 !px-3" data-testid={`void-payment-m-${p.id}`}><Ban className="w-4 h-4" /> Annuler</button></div>
                    : !p.voided ? <div className="mt-3"><span className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B] px-2.5 py-0.5 text-xs font-semibold" data-testid={`payment-validated-m-${p.id}`}><Check className="w-3 h-3" /> Validé</span></div> : null}
                </div>
              ))}
              {visiblePayments.length === 0 && <div className="text-center text-[#94A3B8] py-10">Aucun encaissement{payHideVoided ? " actif" : ""}</div>}
            </div>

            <Pagination page={payPage} setPage={setPayPage} total={visiblePayments.length} />
          </div>
        </div>
      )}

      <Modal open={!!sel} onClose={() => setSel(null)} title={`Encaisser — ${sel?.number}`} testId="collect-modal"
        footer={<><button className="btn-outline" onClick={() => setSel(null)}>Annuler</button>
          <button className="btn-success" onClick={pay} disabled={busy} data-testid="confirm-collect-btn">Valider l'encaissement</button></>}>
        {sel && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm">
              <div className="flex justify-between"><span className="text-[#64748B]">Client</span><span className="font-medium">{sel.customer_name}</span></div>
              <div className="flex justify-between mt-1"><span className="text-[#64748B]">Total vente</span><span>{money(sel.total)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-[#64748B]">Déjà payé</span><span className="text-[#15803D]">{money(sel.paid)}</span></div>
              <div className="flex justify-between mt-1 font-semibold"><span className="text-[#64748B]">Solde restant</span><span className="text-[#B45309]">{money(sel.balance)}</span></div>
            </div>
            <div>
              <label className="lbl">Montant à encaisser</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="inp" data-testid="collect-amount-input" max={sel.balance} />
            </div>
            <div>
              <label className="lbl">Mode de paiement</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="inp" data-testid="collect-method-select">
                <option value="cash">Espèces</option><option value="mobile_money">Mobile Money</option><option value="bank">Banque</option>
              </select>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!saleDetail} onClose={() => setSaleDetail(null)} title="Détails de la vente" wide testId="sale-detail-modal"
        footer={<button className="btn-outline" onClick={() => setSaleDetail(null)}>Fermer</button>}>
        {saleDetail && saleDetail.loading && <p className="text-sm text-[#64748B]">Chargement…</p>}
        {saleDetail && !saleDetail.loading && (
          <div className="space-y-3 text-sm" data-testid="sale-detail-content">
            <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
              <div><span className="text-[#64748B]">Référence</span><div className="font-mono font-medium">{saleDetail.number}</div></div>
              <div><span className="text-[#64748B]">Client</span><div className="font-medium">{saleDetail.customer_name || "—"}</div></div>
              <div><span className="text-[#64748B]">Date / heure</span><div>{fmtDate(saleDetail.created_at)}</div></div>
              <div><span className="text-[#64748B]">Vendeur</span><div>{saleDetail.seller_name || saleDetail.created_by || saleDetail.cashier_name || saleDetail.user_name || "—"}</div></div>
            </div>
            <div className="overflow-x-auto border border-[#E7ECF3] rounded-xl">
              <table className="w-full">
                <thead><tr><th className="th">Produit</th><th className="th text-right">Qté</th><th className="th text-right">P.U.</th><th className="th text-right">Total ligne</th></tr></thead>
                <tbody>
                  {(saleDetail.lines || saleDetail.items || []).map((l, i) => (
                    <tr key={i} className="row"><td className="td">{l.description || l.product_name}</td><td className="td text-right">{l.quantity}</td><td className="td text-right">{money(l.unit_price)}</td><td className="td text-right">{money(l.total)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between font-semibold text-[#0F172A]"><span>Total de la vente</span><span>{money(saleDetail.total)}</span></div>
            <div className="flex items-center justify-between text-[#64748B]"><span>Paiement</span><span className="flex items-center gap-2">{(saleDetail.payment_type === "cash" ? "Comptant" : saleDetail.payment_type === "partial" ? "Partiel" : saleDetail.payment_type === "credit" ? "Crédit" : saleDetail.payment_type) || "—"} <StatusBadge status={saleDetail.status} /></span></div>
          </div>
        )}
      </Modal>

      <VoidReasonDialog open={!!voidTarget} title="Annuler l'encaissement"        message={`Annuler l'encaissement de ${money(voidTarget?.amount || 0)}${voidTarget ? ` pour ${names[voidTarget.customer_id] || "ce client"}` : ""} ? La créance sera rétablie et le solde du client mis à jour. L'opération restera visible avec le statut « Annulé ».`}
        confirmLabel="Annuler l'encaissement" loading={voidLoading} error={voidErr}
        onConfirm={doVoidPayment} onClose={() => setVoidTarget(null)} testId="receivable-void-dialog" />
    </div>
  );
}
